import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { Add } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import {
  useEquipmentActions,
  useEquipmentDelivery,
  EquipmentDeliveryForm,
  EquipmentDelivery,
  Section,
} from 'modules/equipment';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { DealBuilderFormValues } from '../../../types';
import { useEquipmentSearch } from '../../../hooks';
import { useDealBuilderContext } from '../../../context/DealBuilderContext';
import {
  dealBuilderApi,
  InventoryEquipmentItem,
  UpdateDealBuilderQuoteResponse,
  UpdateDealBuilderQuoteVariables,
  UpsertDealBuilderEquipmentsResponse,
  UpsertDealBuilderEquipmentsVariables,
} from '../../../api';
import {
  mapEquipmentSourceToItem,
  mapEquipmentItemToUpdateQuoteEquipmentInput,
  mapQuoteToUpdateQuoteInput,
  isSameDeliveryAddress,
} from '../../../lib';
import {
  DEAL_BUILDER_EQUIPMENT_ACTION_ADD,
  DEAL_BUILDER_EQUIPMENT_ACTION_EDIT,
  DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ACTION,
  DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ID,
} from '../../../constants';
import { StepFooterNav } from '../../components/StepFooterNav';
import { EquipmentSearchBar } from './EquipmentSearchBar';
import { EquipmentItemCard } from './EquipmentItemCard';
import { quoteAPI } from '../../../../quote/api';

export const EquipmentStep = () => {
  const { control, setValue } = useFormContext<DealBuilderFormValues>();
  const selectedId = useWatch({ control, name: 'equipment.selectedId' });
  const { search, results, loading } = useEquipmentSearch();
  const {
    sourceData: { vo, equipments: equipmentData },
    equipment: { selectedEquipmentItem, setSelectedEquipmentItem, deliveryAddressSubmitRef, setEquipmentSaving },
    activeEntity,
    setActiveEntity,
  } = useDealBuilderContext();
  const { handleOpen: openEquipmentDialog, handleClose: closeEquipmentDialog } = useModal(
    ModalsKeys.CreateUpdateEquipmentDialog
  );
  const { onCreateEquipment, onUpdateEquipment } = useEquipmentActions();
  const { replace } = useHistory();
  const location = useLocation();
  const lastSavedAddressRef = useRef<EquipmentDelivery | null>(null);
  const { handleUpdateDeliveryAddress } = useEquipmentDelivery(selectedId ?? undefined, address => {
    lastSavedAddressRef.current = address;
  });

  const [upsertEquipmentsMutation] = useMutation<
    UpsertDealBuilderEquipmentsResponse,
    UpsertDealBuilderEquipmentsVariables
  >(dealBuilderApi.upsertDealBuilderEquipments);
  const [updateQuoteMutation] = useMutation<UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables>(
    dealBuilderApi.updateDealBuilderQuote,
    { refetchQueries: [quoteAPI.getQuoteCalcById], awaitRefetchQueries: true }
  );
  const equipmentsTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleEquipmentChange = useCallback(
    (item: InventoryEquipmentItem | null, overrideProposalId?: string) => {
      const proposalId = overrideProposalId ?? activeEntity?.proposalId;
      if (!item || !proposalId) return;
      clearTimeout(equipmentsTimerRef.current);
      setEquipmentSaving(true);

      equipmentsTimerRef.current = setTimeout(async () => {
        const quoteId = activeEntity?.quote?.quoteId;
        const shouldUpdateQuote = !!(item && quoteId && vo?.vendorOpportunityId);

        const [, updateQuoteResult] = await Promise.all([
          upsertEquipmentsMutation({ variables: { input: { proposalId, equipmentIds: [item.equipment.id] } } }),
          shouldUpdateQuote
            ? updateQuoteMutation({
                variables: {
                  quoteId: quoteId,
                  input: mapQuoteToUpdateQuoteInput(
                    activeEntity.quote,
                    mapEquipmentItemToUpdateQuoteEquipmentInput(item),
                    vo!.vendorOpportunityId
                  ),
                },
              })
            : Promise.resolve(null),
        ]);

        const updatedQuote = updateQuoteResult?.data?.dealBuilder?.updateDealBuilderQuote?.entity;
        if (updatedQuote) setActiveEntity({ ...activeEntity!, quote: updatedQuote });

        setEquipmentSaving(false);
      }, 500);
    },
    [activeEntity, upsertEquipmentsMutation, updateQuoteMutation, setActiveEntity, setEquipmentSaving, vo]
  );

  // reset the tracked address when a different equipment item is selected
  useEffect(() => {
    lastSavedAddressRef.current = null;
  }, [selectedId]);

  const handleDeliveryBlur = useCallback(async () => {
    if (!selectedId || !deliveryAddressSubmitRef.current) return;
    const result = await deliveryAddressSubmitRef.current.submit();
    if (!result.isValid) return;
    if (isSameDeliveryAddress(lastSavedAddressRef.current, result.formValues)) return;
    await handleUpdateDeliveryAddress(result.formValues);
    lastSavedAddressRef.current = result.formValues;
  }, [selectedId, deliveryAddressSubmitRef, handleUpdateDeliveryAddress]);

  const prepopulatedItems = useMemo(() => (equipmentData ?? []).map(mapEquipmentSourceToItem), [equipmentData]);

  const displayItems = useMemo(() => {
    const base = results.length > 0 ? results : prepopulatedItems;
    if (selectedEquipmentItem && !base.some(i => i.equipment.id === selectedEquipmentItem.equipment.id)) {
      return [selectedEquipmentItem, ...base];
    }
    return base;
  }, [results, prepopulatedItems, selectedEquipmentItem]);

  const handleSelect = (id: string) => {
    if (selectedId === id) {
      return;
    } else {
      const item = displayItems.find(i => i.equipment.id === id) ?? null;
      setValue('equipment.selectedId', id);
      setSelectedEquipmentItem(item);
      handleEquipmentChange(item);
    }
  };

  const handleViewUnit = (equipmentId: string) => {
    window.open(`/equipment/${equipmentId}`, '_blank');
  };

  const handleAddManualUnit = () => {
    openEquipmentDialog({
      equipmentId: undefined,
      onCancel: closeEquipmentDialog,
      onSubmit: async ({ createDTO }) => {
        closeEquipmentDialog();
        await onCreateEquipment({
          vendorOpportunityId: vo?.vendorOpportunityId ?? '',
          dynamicsVendorOpportunityId: vo?.dynamicsVendorOpportunityId ?? '',
          ...createDTO,
        });
      },
    });
  };

  const handleEditEquipment = useCallback(
    (equipmentId: string) => {
      openEquipmentDialog({
        equipmentId,
        onCancel: closeEquipmentDialog,
        onSubmit: async ({ updateDTO }) => {
          closeEquipmentDialog();
          await onUpdateEquipment({
            vendorOpportunityId: vo?.vendorOpportunityId ?? '',
            dynamicsVendorOpportunityId: vo?.dynamicsVendorOpportunityId ?? '',
            ...updateDTO,
          });
        },
      });
    },
    [openEquipmentDialog, closeEquipmentDialog, onUpdateEquipment, vo]
  );

  // Opens the create/edit modal when arriving here via a deep-link (e.g. from the Deal Summary card), then clears the params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get(DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ACTION);
    const equipmentId = params.get(DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ID);
    if (!action) return;

    if (action === DEAL_BUILDER_EQUIPMENT_ACTION_ADD) {
      handleAddManualUnit();
    } else if (action === DEAL_BUILDER_EQUIPMENT_ACTION_EDIT && equipmentId) {
      handleEditEquipment(equipmentId);
    }

    params.delete(DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ACTION);
    params.delete(DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ID);
    replace({ pathname: location.pathname, hash: location.hash, search: params.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CardContainer
      title="Add Equipment"
      subTitle="Select units from inventory or add them manually to include in this quote."
    >
      <Stack gap={2}>
        <EquipmentSearchBar onChange={search} />

        {loading && <Typography color="text.secondary">Searching...</Typography>}

        {displayItems.length === 0 ? (
          <Typography variant="subtitle2" component="span" fontWeight={500}>
            No equipment added. Search Inventory or add equipment manually.
          </Typography>
        ) : (
          <Stack gap={1.5}>
            {displayItems.map(item => (
              <EquipmentItemCard
                key={item.equipment.id}
                item={item}
                selected={selectedId === item.equipment.id}
                onSelect={handleSelect}
                onViewUnit={handleViewUnit}
              />
            ))}
          </Stack>
        )}

        <Button size="small" startIcon={<Add />} onClick={handleAddManualUnit} sx={{ alignSelf: 'flex-start' }}>
          Add Manual Unit
        </Button>
      </Stack>

      {selectedId && (
        <Section title="Delivery Address">
          <Box onBlur={handleDeliveryBlur}>
            <EquipmentDeliveryForm equipmentId={selectedId} submitRef={deliveryAddressSubmitRef} />
          </Box>
        </Section>
      )}

      <StepFooterNav />
    </CardContainer>
  );
};
